import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ErrorHandler,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';

const DEFAULT_MASK_OPACITY = 0.6;
const ABORT_ERROR = 'AbortError';
const NOT_ALLOWED_ERROR = 'NotAllowedError';

@Component({
  selector: 'fr8bot-web-video-autoplay',
  templateUrl: './video-autoplay.html',
  styleUrl: './video-autoplay.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoAutoplay implements OnDestroy {
  readonly videoRef = viewChild.required<ElementRef<HTMLVideoElement>>('videoRef');

  src = input('');
  poster = input('');
  maskOpacity = input(DEFAULT_MASK_OPACITY);
  // Above-the-fold video: loads fully and its poster is fetched with high priority.
  priority = input(false);

  videoLoaded = signal(false);

  private readonly errorHandler = inject(ErrorHandler);
  private observer: IntersectionObserver | null = null;
  private playFailureReported = false;

  constructor() {
    afterNextRender(() => this.initObserver());
  }

  private initObserver() {
    const video = this.videoRef()?.nativeElement;
    if (!video) return;

    video.muted = true;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.play(video);
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.1 },
    );

    this.observer.observe(video);
  }

  // Scrolling past a still-buffering video pauses it (AbortError), and the browser may block
  // autoplay (NotAllowedError) — the poster stays in both cases, so neither is an error.
  private play(video: HTMLVideoElement) {
    void video.play().catch((error: unknown) => {
      if (
        error instanceof DOMException &&
        (error.name === ABORT_ERROR || error.name === NOT_ALLOWED_ERROR)
      ) {
        return;
      }
      // Anything else is a broken video (missing file, unsupported codec): report it once, with
      // its source, instead of on every scroll back into view.
      if (this.playFailureReported) return;
      this.playFailureReported = true;
      this.errorHandler.handleError(
        new Error(`Landing video failed to play: ${this.src()}`, { cause: error }),
      );
    });
  }

  setVideoLoaded() {
    this.videoLoaded.set(true);
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    this.observer = null;
  }
}
